/*
 * Productgalerij: de video start zichzelf, geeft daarna het beeld door en
 * begint opnieuw zodra de bezoeker terugkomt.
 *
 * Broadcast houdt productvideo's in een <template> binnen <deferred-media>;
 * pas een klik op de poster zet de <video> in de DOM. Dat mechanisme hoeft
 * niet nagebouwd te worden -- deferredMedia.loadContent(false) doet precies
 * hetzelfde als die klik, alleen zonder de focus() die de pagina zou laten
 * verspringen bij het laden.
 *
 * De volgorde is het hele punt: loadContent() kloont de template en roept in
 * dezelfde stap play() aan. Een niet-gedempte video wordt daar door de browser
 * geweigerd, dus muted en playsinline moeten al in de template staan voordat
 * er gekloond wordt.
 *
 * Doorschakelen na afloop gaat via theme:media:select, hetzelfde event dat de
 * thumbnails en de variantwissel gebruiken. Zo blijft er precies een slider.
 *
 * De bediening is eigen werk: media.liquid geeft de video mee met
 * controls: false, want de balk van de browser past niet bij de rest van de
 * pagina. De knoppen hieronder worden pas gebouwd als de video echt in de
 * DOM staat, en werken ook voor een video die de bezoeker zelf aanklikt.
 *
 * Let op: staat "Enable video looping" aan in de sectie-instellingen, dan
 * krijgt de video een loop-attribuut en vuurt 'ended' nooit. Het doorschakelen
 * naar de eerste foto werkt dan niet -- dat is de instelling, niet een fout.
 */
(function () {
  'use strict';

  var SECTION = '[data-section-type="product"]';
  var GALLERY = 'product-images';
  var SLIDE = '.product__slide';
  var MEDIA_LIST = '[data-product-media-list]';
  var ACTIVE = 'media--active';

  var ICON_PLAY = '<svg class="brand-video-controls__icon brand-video-controls__icon--play" viewBox="0 0 12 14" aria-hidden="true" focusable="false"><path d="M1 1.2c0-.5.5-.8 1-.6l9 5.8c.4.3.4.9 0 1.2l-9 5.8c-.5.3-1 0-1-.6V1.2z"/></svg>';
  var ICON_PAUSE = '<svg class="brand-video-controls__icon brand-video-controls__icon--pause" viewBox="0 0 12 14" aria-hidden="true" focusable="false"><rect x="1" y="1" width="3.5" height="12" rx="1"/><rect x="7.5" y="1" width="3.5" height="12" rx="1"/></svg>';
  var ICON_SOUND = '<svg class="brand-video-controls__icon brand-video-controls__icon--sound" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M7.3 1.3 4 4H1.8c-.4 0-.8.4-.8.8v4.4c0 .4.4.8.8.8H4l3.3 2.7c.5.4 1.2 0 1.2-.6V1.9c0-.6-.7-1-1.2-.6z"/><path d="M10.4 4.3a.75.75 0 0 0-1 1.1c.5.4.8 1 .8 1.6s-.3 1.2-.8 1.6a.75.75 0 1 0 1 1.1c.8-.7 1.3-1.7 1.3-2.7s-.5-2-1.3-2.7z"/></svg>';
  var ICON_MUTED = '<svg class="brand-video-controls__icon brand-video-controls__icon--muted" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M7.3 1.3 4 4H1.8c-.4 0-.8.4-.8.8v4.4c0 .4.4.8.8.8H4l3.3 2.7c.5.4 1.2 0 1.2-.6V1.9c0-.6-.7-1-1.2-.6z"/><path d="M13.3 5.5a.7.7 0 0 0-1-1l-1.3 1.3-1.3-1.3a.7.7 0 1 0-1 1L10 6.8 8.7 8.1a.7.7 0 1 0 1 1L11 7.8l1.3 1.3a.7.7 0 0 0 1-1L12 6.8l1.3-1.3z"/></svg>';

  function windowWidth() {
    return document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth;
  }

  function isMobile() {
    var sizes = (window.theme && window.theme.sizes) || {};
    return windowWidth() < (sizes.small || 750);
  }

  function activeSlide(gallery) {
    return (
      gallery.querySelector(SLIDE + '.' + ACTIVE) ||
      gallery.querySelector('[data-media-id="' + gallery.getAttribute('data-active-media') + '"]')
    );
  }

  /* Doorschakelen naar de eerste foto in de galerij. */
  function advance(gallery) {
    var target = gallery.querySelector(SLIDE + '[data-type="image"]');
    if (!target || target.classList.contains(ACTIVE)) return;

    var id = target.getAttribute('data-media-id');
    if (!id) return;

    // Het eigen kanaal van het thema: ProductImages luistert hierop en regelt
    // de actieve klasse, de hoogte van de houder en de actieve thumbnail.
    target.dispatchEvent(
      new CustomEvent('theme:media:select', {
        bubbles: true,
        detail: {id: id},
      })
    );

    // Zonder fader staan alle slides naast elkaar in een scrollrij en
    // verplaatsen die klassen niets. theme.js schuift in dat geval de lijst
    // zelf; dat wordt hier gespiegeld.
    if (isMobile() && !gallery.hasAttribute('data-fader-mobile')) {
      var list = gallery.querySelector(MEDIA_LIST);
      if (list) {
        list.scrollTo({left: target.offsetLeft, behavior: 'smooth'});
      }
    }
  }

  /* ---------------------------------------------------------------- */
  /* Eigen bediening                                                   */
  /* ---------------------------------------------------------------- */

  function button(className, label, markup) {
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'brand-video-controls__btn ' + className;
    el.setAttribute('aria-label', label);
    el.innerHTML = markup;
    return el;
  }

  function buildControls(deferred, video) {
    if (deferred.querySelector('.brand-video-controls')) return;

    var bar = document.createElement('div');
    bar.className = 'brand-video-controls';
    bar.setAttribute('data-playing', video.paused ? 'false' : 'true');
    bar.setAttribute('data-muted', video.muted ? 'true' : 'false');

    var play = button('brand-video-controls__btn--play', 'Play video', ICON_PLAY + ICON_PAUSE);
    var sound = button('brand-video-controls__btn--sound', 'Unmute video', ICON_SOUND + ICON_MUTED);

    // Een echte range: pijltjestoetsen, Home/End en schermlezers werken dan
    // vanzelf. De gevulde helft is een achtergrondverloop op --brand-progress.
    var seek = document.createElement('input');
    seek.type = 'range';
    seek.className = 'brand-video-controls__seek';
    seek.min = '0';
    seek.max = '100';
    seek.step = '0.1';
    seek.value = '0';
    seek.setAttribute('aria-label', 'Seek video');

    bar.appendChild(play);
    bar.appendChild(seek);
    bar.appendChild(sound);
    deferred.appendChild(bar);

    // De galerij zelf luistert op klikken; de bediening hoort daar los van
    // te staan, anders wisselt een klik op pauze ook nog van slide.
    bar.addEventListener('click', function (event) {
      event.stopPropagation();
    });

    play.addEventListener('click', function () {
      if (video.paused) {
        video.play().catch(function () {});
      } else {
        video.pause();
      }
    });

    sound.addEventListener('click', function () {
      video.muted = !video.muted;
    });

    // Tik op de video zelf: pauzeer of hervat. Zijn wens (20-09-2026): de
    // bediening staat uit beeld terwijl de video speelt en komt pas bij een tik.
    // Pauzeren zet data-playing="false", en de CSS toont de balk dan vanzelf;
    // hervatten verbergt hem weer (op touch, waar er geen hover is).
    // stopPropagation: de galerij luistert op klikken voor zoom en slidewissel,
    // en een tik op de video hoort hem te pauzeren, niet te zoomen.
    video.addEventListener('click', function (event) {
      event.stopPropagation();
      if (video.paused) {
        video.play().catch(function () {});
      } else {
        video.pause();
      }
    });

    seek.addEventListener('input', function () {
      if (!isFinite(video.duration) || !video.duration) return;
      video.currentTime = (parseFloat(seek.value) / 100) * video.duration;
    });

    function paint() {
      var pct = 0;
      if (isFinite(video.duration) && video.duration > 0) {
        pct = (video.currentTime / video.duration) * 100;
      }
      seek.value = String(pct);
      bar.style.setProperty('--brand-progress', pct.toFixed(2) + '%');
    }

    function state() {
      bar.setAttribute('data-playing', video.paused ? 'false' : 'true');
      bar.setAttribute('data-muted', video.muted ? 'true' : 'false');
      play.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    }

    video.addEventListener('timeupdate', paint);
    video.addEventListener('loadedmetadata', paint);
    video.addEventListener('seeked', paint);
    video.addEventListener('play', state);
    video.addEventListener('pause', state);
    video.addEventListener('volumechange', state);
    video.addEventListener('ended', function () {
      // Na afloop staat de balk vol; de slide wisselt toch weg.
      paint();
      state();
    });

    paint();
    state();
  }

  /* ---------------------------------------------------------------- */
  /* Laden, starten, hervatten                                         */
  /* ---------------------------------------------------------------- */

  /*
   * De template aanpassen voor loadContent() hem kloont. Attributen, geen
   * properties: een property overleeft cloneNode() niet.
   */
  function primeTemplate(deferred) {
    var template = deferred.querySelector('template');
    if (!template || !template.content) return;

    var video = template.content.querySelector('video');
    if (video) {
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      return;
    }

    var iframe = template.content.querySelector('iframe');
    if (iframe) {
      iframe.setAttribute('src', externalSrc(iframe.getAttribute('src') || ''));
    }
  }

  /*
   * Safari (10-10-2026): een video die uit een <template> gekloond wordt is
   * daar niet gedempt, ook al staat muted erop -- WebKit leest dat attribuut
   * alleen bij het parsen. loadContent() roept dan play() aan op een video
   * met geluid, en die wordt geweigerd. Daarom kloont dit de video zelf en
   * zet de muted-PROPERTY voordat hij de pagina in gaat. Hetzelfde als
   * loadContent(false), minus die ene play().
   */
  function loadNative(deferred) {
    var template = deferred.querySelector('template');
    var source = template && template.content && template.content.querySelector('video');
    if (!source) return false;

    if (typeof deferred.pauseAllMedia === 'function') deferred.pauseAllMedia();

    var video = source.cloneNode(true);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    deferred.setAttribute('loaded', true);
    deferred.appendChild(video);
    return true;
  }

  function ensureLoaded(deferred) {
    if (deferred.querySelector('video, iframe')) return;

    if (loadNative(deferred)) return;

    primeTemplate(deferred);

    if (typeof deferred.loadContent === 'function') {
      deferred.loadContent(false);
    } else {
      // Terugval als het custom element om wat voor reden dan ook niet is
      // geupgraded; dit is exact de route die een bezoekersklik neemt.
      var poster = deferred.querySelector('[data-deferred-media-button]');
      if (poster) poster.click();
    }
  }

  /* Shopify-video: dempen, playsinline, het einde afvangen en starten. */
  function handleNative(video, deferred, gallery) {
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    if (!video.brandEndBound) {
      video.brandEndBound = true;
      video.addEventListener('ended', function () {
        advance(gallery);
      });
    }

    buildControls(deferred, video);

    // Terug bij de video betekent opnieuw kijken, niet de laatste frame.
    try {
      video.currentTime = 0;
    } catch (e) {
      /* metadata nog niet binnen; de browser begint dan zelf bij 0 */
    }

    tryPlay(video, deferred);
  }

  /*
   * Weigert de browser, dan eerst nog een keer zodra er data is: met
   * preload="none" kan Safari de eerste play() te vroeg vinden. Weigert hij
   * dan nog (iPhone in energiebesparingsmodus speelt nooit vanzelf af), dan
   * start de video bij de eerste tik ergens op de pagina -- die telt als
   * gebaar. Tot dan blijft de posterframe met bediening staan.
   */
  function tryPlay(video, deferred) {
    var started = video.play();
    if (!started || typeof started.catch !== 'function') return;

    started.catch(function () {
      // Meteen, niet pas na canplay: met preload="none" laadt een geweigerde
      // video niets, dus canplay komt dan nooit.
      onFirstGesture(video, deferred);
      video.addEventListener(
        'canplay',
        function () {
          if (video.paused && isShown(deferred)) video.play().catch(function () {});
        },
        {once: true}
      );
    });
  }

  function isShown(deferred) {
    var slide = deferred.closest(SLIDE);
    return !slide || slide.classList.contains(ACTIVE) || isMobile();
  }

  function onFirstGesture(video, deferred) {
    if (video.brandGestureBound) return;
    video.brandGestureBound = true;

    var events = ['touchend', 'click', 'keydown'];
    function go(event) {
      events.forEach(function (name) {
        document.removeEventListener(name, go, true);
      });
      video.brandGestureBound = false;
      // Een tik op de video zelf regelt zijn eigen klikhandler al.
      if (event.target && deferred.contains(event.target)) return;
      if (video.paused && isShown(deferred)) video.play().catch(function () {});
    }
    events.forEach(function (name) {
      document.addEventListener(name, go, {capture: true, passive: true});
    });
  }

  /* YouTube/Vimeo laten alleen autoplay toe als de embed zelf gedempt start. */
  function externalSrc(src) {
    if (!src) return src;

    var isVimeo = src.indexOf('vimeo') !== -1;
    var extra = [];

    if (isVimeo) {
      if (src.indexOf('muted=') === -1) extra.push('muted=1');
    } else {
      if (src.indexOf('mute=') === -1) extra.push('mute=1');
      // Zonder enablejsapi stuurt de speler geen statusberichten terug.
      if (src.indexOf('enablejsapi=') === -1) extra.push('enablejsapi=1');
    }

    if (src.indexOf('playsinline=') === -1) extra.push('playsinline=1');

    if (!extra.length) return src;

    return src + (src.indexOf('?') === -1 ? '?' : '&') + extra.join('&');
  }

  /*
   * Einde-detectie bij externe spelers gaat via postMessage. Beide diensten
   * sturen pas iets terug na een handdruk, en YouTube alleen als de speler al
   * klaar is -- vandaar het herhalen tot de eerste reactie binnen is.
   */
  function handleExternal(iframe, gallery) {
    if (iframe.brandEndBound) return;
    iframe.brandEndBound = true;

    var isVimeo = iframe.getAttribute('data-host') === 'vimeo';
    var tries = 0;
    var timer = null;

    function post(message) {
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage(JSON.stringify(message), '*');
        }
      } catch (e) {
        /* cross-origin voordat de speler klaar is */
      }
    }

    function handshake() {
      if (isVimeo) {
        post({method: 'addEventListener', value: 'ended'});
      } else {
        post({event: 'listening', id: iframe.id || 'brand-pdp-video'});
      }
    }

    function stopHandshake() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    window.addEventListener('message', function (event) {
      var origin = event.origin || '';
      if (origin.indexOf('youtube') === -1 && origin.indexOf('vimeo') === -1) return;

      var data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return;
        }
      }
      if (!data) return;

      stopHandshake();

      var ended =
        data.event === 'ended' ||
        (data.event === 'onStateChange' && data.info === 0) ||
        (data.event === 'infoDelivery' && data.info && data.info.playerState === 0);

      if (ended) advance(gallery);
    });

    iframe.addEventListener('load', handshake);
    handshake();

    timer = setInterval(function () {
      tries += 1;
      if (tries > 12) {
        stopHandshake();
        return;
      }
      handshake();
    }, 500);
  }

  /* Een externe speler kent hier geen currentTime; opnieuw laden is het
     enige wat vanaf buitenaf gegarandeerd terugspoelt. */
  function restartExternal(iframe) {
    if (!iframe.brandShown) {
      iframe.brandShown = true;
      return;
    }
    var src = iframe.getAttribute('src');
    if (src) iframe.setAttribute('src', src);
  }

  /* De slide is in beeld gekomen: laden als het moet, en van voren af aan. */
  function activate(slide, gallery) {
    var deferred = slide.querySelector('deferred-media');
    if (!deferred) return;

    ensureLoaded(deferred);

    var video = deferred.querySelector('video');
    if (video) {
      handleNative(video, deferred, gallery);
      return;
    }

    var iframe = deferred.querySelector('iframe');
    if (iframe) {
      handleExternal(iframe, gallery);
      restartExternal(iframe);
    }
  }

  /* Uit beeld: stilzetten. Terugspoelen gebeurt pas bij terugkomst, zodat
     de laatste frame niet even zichtbaar terugspringt tijdens het wisselen. */
  function deactivate(slide) {
    var video = slide.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  /*
   * ProductImages wisselt van slide door media--active te verplaatsen. Daar
   * is geen event voor, dus wordt de klasse zelf in de gaten gehouden.
   */
  function watchSlide(slide, gallery) {
    if (slide.brandWatched) return;
    slide.brandWatched = true;
    slide.brandActive = slide.classList.contains(ACTIVE);

    new MutationObserver(function () {
      var active = slide.classList.contains(ACTIVE);
      if (active === slide.brandActive) return;
      slide.brandActive = active;

      if (active) {
        activate(slide, gallery);
      } else {
        deactivate(slide);
      }
    }).observe(slide, {attributes: true, attributeFilter: ['class']});

    // In sliderweergave blijven alle slides zichtbaar en verschuift alleen de
    // scrollpositie; dan bepaalt het beeld zelf wanneer de video aan de beurt is.
    if (!gallery.hasAttribute('data-fader-mobile') && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!isMobile()) return;
            if (entry.isIntersecting) {
              activate(slide, gallery);
            } else {
              deactivate(slide);
            }
          });
        },
        {threshold: 0.6}
      ).observe(slide);
    }
  }

  /*
   * Vegen over de video. Broadcast wisselt bij een veeg alleen van slide als
   * de huidige slide data-type="image" heeft (handleMouseUp in theme.js), dus
   * op de video gebeurde niets en zat je op mobiel vast (zijn melding,
   * 14-10-2026). Hier dezelfde drempel als Broadcast -- meer dan 10 px en meer
   * opzij dan omhoog -- maar alleen voor slides die geen foto zijn, zodat een
   * veeg over een foto niet twee keer telt.
   */
  function bindSwipe(gallery, list) {
    if (!list || list.brandSwipe) return;
    list.brandSwipe = true;

    var startX = null;
    var startY = 0;
    var startSlide = null;

    // De slide waarop de veeg begint telt, niet die bij touchend: Broadcasts
    // eigen handler loopt eerst en kan dan al naar de video zijn gesprongen,
    // waarna deze handler nog een keer zou wisselen.
    list.addEventListener(
      'touchstart',
      function (event) {
        var touch = event.changedTouches[0];
        startX = touch.screenX;
        startY = touch.screenY;
        startSlide = typeof gallery.getCurrentMedia === 'function' ? gallery.getCurrentMedia() : null;
      },
      {passive: true}
    );

    list.addEventListener(
      'touchend',
      function (event) {
        if (startX === null) return;
        var touch = event.changedTouches[0];
        var dx = touch.screenX - startX;
        var dy = touch.screenY - startY;
        startX = null;

        var current = startSlide;
        startSlide = null;
        if (!current || current.getAttribute('data-type') === 'image') return;
        if (Math.abs(dx) <= 10 || Math.abs(dx) <= Math.abs(dy)) return;

        if (dx < 0) {
          gallery.showNextImage();
        } else {
          gallery.showPreviousImage();
        }
      },
      {passive: true}
    );
  }

  function initGallery(gallery) {
    if (gallery.brandVideoInit) return;
    gallery.brandVideoInit = true;

    var slides = gallery.querySelectorAll(SLIDE + '[data-video]');
    if (!slides.length) return;

    bindSwipe(gallery, gallery.querySelector(MEDIA_LIST));

    slides.forEach(function (slide) {
      watchSlide(slide, gallery);
    });

    var current = activeSlide(gallery);
    if (current && current.hasAttribute('data-video')) {
      activate(current, gallery);
    }
  }

  function run() {
    document.querySelectorAll(SECTION + ' ' + GALLERY).forEach(initGallery);
  }

  /*
   * Dit bestand staat in de documentvolgorde voor theme.js en beide zijn
   * defer, dus het draait eerder: de custom elements bestaan dan nog niet.
   * De race voorkomt dat we blijven wachten als een definitie uitblijft.
   */
  function whenDefined(name) {
    if (!window.customElements) return Promise.resolve();

    return Promise.race([
      window.customElements.whenDefined(name),
      new Promise(function (resolve) {
        setTimeout(resolve, 3000);
      }),
    ]);
  }

  function boot() {
    Promise.all([whenDefined('deferred-media'), whenDefined('product-images')]).then(run);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
